import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { AppContent } from "./components/AppContent/AppContent.jsx";
import { AppShell } from "./components/AppShell/AppShell.jsx";
import { createBudgetPageData } from "./data/budget-page.js";
import { createAppShortcutBindings } from "./lib/app/appShortcutBindings.js";
import { buildHeaderAlert, buildHeaderTabs } from "./lib/app/appShellViewModel.js";
import { createWalletWatchActions, getPreviousBudgetYear } from "./lib/app/createWalletWatchActions.js";
import {
  buildHashForRoute,
  pageSectionConfig,
  rememberSection,
  routeFromHash
} from "./lib/routing/appRouteState.js";
import { useWalletWatchBootstrap } from "./lib/app/useWalletWatchBootstrap.js";
import {
  BACKUP_ATTENTION_CHANGED_EVENT,
  getProfileBackupNeedsAttention,
  installProfileAutoBackup
} from "./lib/storage/profileBackupFile.js";
import { useEscapeStack } from "./lib/shortcuts/useEscapeStack.js";
import { useAltHintVisibility } from "./lib/shortcuts/useAltHintVisibility.js";
import { useAppShortcuts } from "./lib/shortcuts/useAppShortcuts.js";
import { usePageStateMemory } from "./lib/usePageStateMemory.js";
import { DatabaseErrorPage } from "./pages/DatabaseErrorPage.jsx";
import {
  APP_THEME_SYSTEM
} from "./lib/settings/appSettings.js";
import { setFormattingPreferences } from "./lib/formatters.js";
import { createScopeDateContext } from "./lib/scopeDate.js";
import { listBudgetRows } from "./lib/storage/walletWatchDb.js";
import { checkForAppUpdate } from "./lib/appVersion.js";
import { createTranslator, getDirectionForLanguage, getTranslationNode, resolveAppLanguage } from "./i18n/index.js";

const THEME_STORAGE_KEY = "wallet-watch:theme";

function readStoredTheme() {
  try {
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
      return null;
    }

    return window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

function applyStoredThemePreference() {
  if (typeof document === "undefined") {
    return;
  }

  const root = document.documentElement;
  const storedTheme = readStoredTheme();

  if (!storedTheme || storedTheme === APP_THEME_SYSTEM) {
    root.removeAttribute("data-theme");
    return;
  }

  root.setAttribute("data-theme", storedTheme);
}

applyStoredThemePreference();

const SHOW_VIEWPORT_GUARD = true;
const SHOW_VIEWPORT_DEBUG_BADGE = false;
const MIN_SUPPORTED_WIDTH = 900;
const MIN_SUPPORTED_HEIGHT = 450;
const CONTEXTUAL_RETURN_STACK_SESSION_KEY = "wallet-watch:contextual-return-stack";

function setBootThemePreference(theme) {
  try {
    if (!theme || theme === APP_THEME_SYSTEM) {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // ignore local storage failures
  }
}

function isCompleteBudgetRow(row) {
  return Boolean(String(row.category ?? "").trim())
    && Number.isFinite(Number(row.ongoingBudget))
    && Number.isFinite(Number(row.irregularBudget));
}

function findScopeStartDate(scopeYear, expenseRows = [], now = new Date()) {
  const parsedYear = Number(scopeYear);
  const currentYear = now.getFullYear();
  const scopedYear = Number.isInteger(parsedYear) ? parsedYear : currentYear;
  const effectiveEndTime = (scopedYear === currentYear ? now : new Date(scopedYear, 11, 31)).getTime();
  let earliestTime = Number.POSITIVE_INFINITY;

  expenseRows.forEach((row) => {
    const parsedDate = new Date(`${row.date}T00:00:00`);

    if (Number.isNaN(parsedDate.getTime()) || parsedDate.getFullYear() !== scopedYear) {
      return;
    }

    if (parsedDate.getTime() > effectiveEndTime) {
      return;
    }

    earliestTime = Math.min(earliestTime, parsedDate.getTime());
  });

  return Number.isFinite(earliestTime) ? new Date(earliestTime) : null;
}

function deserializeContextualReturnStack(storedValue) {
  try {
    const parsed = JSON.parse(storedValue);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter((entry) => entry && typeof entry === "object")
      .map((entry) => ({
        pageKey: String(entry.pageKey ?? ""),
        sectionKey: entry.sectionKey ? String(entry.sectionKey) : null
      }))
      .filter((entry) => entry.pageKey);
  } catch {
    return [];
  }
}

function serializeContextualReturnStack(value) {
  return JSON.stringify(Array.isArray(value) ? value : []);
}

export function App() {
  const initialRoute = routeFromHash();
  const [currentPage, setCurrentPage] = useState(initialRoute.pageKey);
  const [currentSections, setCurrentSections] = useState(initialRoute.sections);
  const [contextualReturnStack, setContextualReturnStack] = usePageStateMemory(
    CONTEXTUAL_RETURN_STACK_SESSION_KEY,
    [],
    {
      deserialize: deserializeContextualReturnStack,
      serialize: serializeContextualReturnStack
    }
  );
  const [backupNeedsAttention, setBackupNeedsAttention] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [budgetDraftEntryRequest, setBudgetDraftEntryRequest] = useState(0);
  const [hasPreviousYearBudgetRows, setHasPreviousYearBudgetRows] = useState(false);
  const [viewportSize, setViewportSize] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight
  }));
  const showAltHints = useAltHintVisibility();
  const {
    appSettings,
    availableScopeYears,
    budgetRows,
    budgetRowsRef,
    budgetScope,
    expenseRows,
    isBudgetReady,
    profileSettings,
    bootstrapError,
    reloadAvailableScopeYears,
    reloadBudgetRows,
    reloadExpenseRows,
    setAppSettings,
    setAvailableScopeYears,
    setBudgetRows,
    setBudgetScope,
    setExpenseRows,
    setProfileSettings,
    setShouldShowWelcome,
    shouldShowWelcome
  } = useWalletWatchBootstrap({ initialRoute });
  const latestSaveRequestRef = useRef(0);
  const pendingNavigationModeRef = useRef(null);
  const activeLanguage = resolveAppLanguage(appSettings.language);
  const appDirection = getDirectionForLanguage(activeLanguage);
  const t = useMemo(() => createTranslator(activeLanguage), [activeLanguage]);
  const getTranslationNodeForLanguage = useMemo(
    () => (key) => getTranslationNode(activeLanguage, key),
    [activeLanguage]
  );
  const viewportTooSmall = viewportSize.width < MIN_SUPPORTED_WIDTH || viewportSize.height < MIN_SUPPORTED_HEIGHT;
  const shouldShowViewportGuard = SHOW_VIEWPORT_GUARD && viewportTooSmall;
  const canRotateViewportToFit = viewportSize.height >= MIN_SUPPORTED_WIDTH
    && viewportSize.width >= MIN_SUPPORTED_HEIGHT;
  const isDatabaseErrorMode = Boolean(bootstrapError) || currentPage === "db-error";
  const effectiveCurrentPage = isDatabaseErrorMode ? "db-error" : currentPage;

  useEffect(() => {
    const handleHashChange = () => {
      if (updateAvailable) {
        window.location.reload();
        return;
      }

      if (bootstrapError) {
        if (window.location.hash !== "#/db-error") {
          window.history.replaceState(null, "", "#/db-error");
        }
        setCurrentPage("db-error");
        return;
      }

      const nextRoute = routeFromHash();
      const navigationMode = pendingNavigationModeRef.current;
      if (navigationMode !== "contextual" && navigationMode !== "stack-pop") {
        setContextualReturnStack([]);
      }
      pendingNavigationModeRef.current = null;
      setCurrentPage(nextRoute.pageKey);
      setCurrentSections(nextRoute.sections);
      Object.entries(nextRoute.sections).forEach(([pageKey, section]) => {
        rememberSection(pageKey, section);
      });
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [bootstrapError, setContextualReturnStack, updateAvailable]);

  useEffect(() => installProfileAutoBackup(), []);

  useEffect(() => {
    let cancelled = false;

    async function refreshBackupAttention(profileId) {
      const nextValue = await getProfileBackupNeedsAttention(profileId);

      if (!cancelled) {
        setBackupNeedsAttention(nextValue);
      }
    }

    refreshBackupAttention(budgetScope?.profileId).catch((error) => {
      console.error("Failed to load backup attention state.", error);
    });

    const handleBackupAttentionChanged = (event) => {
      if (event?.detail?.profileId !== budgetScope?.profileId) {
        return;
      }

      setBackupNeedsAttention(Boolean(event.detail.needsAttention));
    };

    window.addEventListener(BACKUP_ATTENTION_CHANGED_EVENT, handleBackupAttentionChanged);
    return () => {
      cancelled = true;
      window.removeEventListener(BACKUP_ATTENTION_CHANGED_EVENT, handleBackupAttentionChanged);
    };
  }, [budgetScope?.profileId]);

  useEffect(() => {
    let cancelled = false;

    checkForAppUpdate().then((result) => {
      if (!cancelled) {
        setUpdateAvailable(Boolean(result.updateAvailable));
      }
    }).catch((error) => {
      console.error("Failed to check for an app update.", error);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    Object.entries(currentSections).forEach(([pageKey, section]) => {
      rememberSection(pageKey, section);
    });
  }, [currentSections]);

  useEffect(() => {
    const nextHash = buildHashForRoute(effectiveCurrentPage, currentSections);

    if (window.location.hash !== nextHash) {
      window.history.replaceState(null, "", nextHash);
    }
  }, [currentSections, effectiveCurrentPage]);

  useEffect(() => {
    if (!shouldShowWelcome || bootstrapError) {
      return;
    }

    setCurrentPage("help");
    setCurrentSections((current) => ({
      ...current,
      help: "tips"
    }));
    window.history.replaceState(null, "", "#/help/tips");
    setShouldShowWelcome(false);
  }, [bootstrapError, setShouldShowWelcome, shouldShowWelcome]);

  useEffect(() => {
    setFormattingPreferences({
      language: appSettings.language,
      currency: profileSettings.currency
    });
  }, [appSettings.language, profileSettings.currency]);

  useEffect(() => {
    const root = document.documentElement;

    if (!isBudgetReady) {
      return;
    }

    if (appSettings.theme === "system") {
      root.removeAttribute("data-theme");
      return;
    }

    root.setAttribute("data-theme", appSettings.theme);
  }, [appSettings.theme, isBudgetReady]);

  useEffect(() => {
    setBootThemePreference(appSettings.theme);
  }, [appSettings.theme]);

  useEffect(() => {
    let cancelled = false;

    async function refreshPreviousYearBudgetState() {
      const previousYear = getPreviousBudgetYear(budgetScope?.year);

      if (!budgetScope || !previousYear || budgetRows.length > 0 || !availableScopeYears.includes(previousYear)) {
        setHasPreviousYearBudgetRows(false);
        return;
      }

      const previousRows = await listBudgetRows(budgetScope.profileId, previousYear);

      if (!cancelled) {
        setHasPreviousYearBudgetRows(previousRows.length > 0);
      }
    }

    refreshPreviousYearBudgetState().catch((error) => {
      console.error("Failed to check previous-year budget rows.", error);
      if (!cancelled) {
        setHasPreviousYearBudgetRows(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [availableScopeYears, budgetRows.length, budgetScope]);

  useEffect(() => {
    const root = document.documentElement;
    root.lang = activeLanguage;
    root.dir = appDirection;
    document.body.dir = appDirection;
  }, [activeLanguage, appDirection]);

  useEffect(() => {
    if (!SHOW_VIEWPORT_GUARD && !SHOW_VIEWPORT_DEBUG_BADGE) {
      return undefined;
    }

    const handleResize = () => {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  function navigateToPage(pageKey, sectionKey = null, options = {}) {
    if (updateAvailable) {
      window.location.reload();
      return;
    }

    if (isDatabaseErrorMode && pageKey !== "db-error") {
      return;
    }

    const navigationMode = options?.navigationMode
      ?? (options?.contextual ? "contextual" : "standard");
    pendingNavigationModeRef.current = navigationMode;

    if (navigationMode === "contextual") {
      setContextualReturnStack((current) => current.concat({
        pageKey: currentPage,
        sectionKey: pageSectionConfig[currentPage]
          ? (currentSections[currentPage] ?? pageSectionConfig[currentPage].defaultSection)
          : null
      }));
    } else if (navigationMode === "standard") {
      setContextualReturnStack([]);
    }

    if (sectionKey && pageSectionConfig[pageKey]) {
      navigateToSection(pageKey, sectionKey, { navigationMode, skipContextPush: true });
      return;
    }

    setCurrentPage(pageKey);
    window.location.hash = buildHashForRoute(pageKey, currentSections);
  }

  function requestBudgetDraftEntry() {
    setBudgetDraftEntryRequest((current) => current + 1);
  }

  function navigateToSection(pageKey, sectionKey, options = {}) {
    if (updateAvailable) {
      window.location.reload();
      return;
    }

    if (isDatabaseErrorMode) {
      return;
    }

    const navigationMode = options?.navigationMode
      ?? (options?.contextual ? "contextual" : "standard");
    pendingNavigationModeRef.current = navigationMode;

    const config = pageSectionConfig[pageKey];
    if (!config) {
      return;
    }

    if (navigationMode === "contextual" && !options?.skipContextPush) {
      setContextualReturnStack((current) => current.concat({
        pageKey: currentPage,
        sectionKey: pageSectionConfig[currentPage]
          ? (currentSections[currentPage] ?? pageSectionConfig[currentPage].defaultSection)
          : null
      }));
    } else if (navigationMode === "standard") {
      setContextualReturnStack([]);
    }

    const nextSection = config.validSections.has(sectionKey) ? sectionKey : config.defaultSection;
    rememberSection(pageKey, nextSection);
    window.location.hash = `#/${pageKey}/${nextSection}`;
    setCurrentPage(pageKey);
    setCurrentSections((current) => ({
      ...current,
      [pageKey]: nextSection
    }));
  }

  useEscapeStack(() => {
    if (isDatabaseErrorMode) {
      return false;
    }

    if (contextualReturnStack.length === 0) {
      return false;
    }

    const nextFrame = contextualReturnStack[contextualReturnStack.length - 1];
    setContextualReturnStack((current) => current.slice(0, -1));
    if (!nextFrame) {
      return false;
    }

    navigateToPage(nextFrame.pageKey, nextFrame.sectionKey, { navigationMode: "stack-pop" });
    return true;
  }, contextualReturnStack.length > 0 && !isDatabaseErrorMode, -1000);

  function navigateSidebarSection(offset) {
    if (isDatabaseErrorMode) {
      return;
    }

    const config = pageSectionConfig[currentPage];
    if (!config) {
      return;
    }

    const sectionValues = Array.from(config.validSections);
    const activeSection = currentSections[currentPage] ?? config.defaultSection;
    const activeIndex = sectionValues.indexOf(activeSection);
    if (activeIndex < 0) {
      return;
    }

    const nextIndex = activeIndex + offset;
    if (nextIndex < 0 || nextIndex >= sectionValues.length) {
      return;
    }

    navigateToSection(currentPage, sectionValues[nextIndex]);
  }

  const {
    cycleToNextYear,
    handleAnalysisBudgetReviewFilterChange,
    handleAnalysisChartsMetricChange,
    handleAnalysisExplorerPresetChange,
    handleBudgetRowsChange,
    handleBudgetViewModeChange,
    handleCurrencyChange,
    handleDataImported,
    handleExpensesChanged,
    handleExpensesVisibilityFilterChange,
    handleHomeTileConfigsChange,
    handleHeaderYearChange,
    handleImportPreviousYearBudget,
    handleLanguageChange,
    handleLoadSampleData,
    handleStartBudgetEntry,
    handleScopeChanged,
    handleThemeChange
  } = createWalletWatchActions({
    appSettings,
    availableScopeYears,
    budgetRowsRef,
    budgetScope,
    isCompleteBudgetRow,
    latestSaveRequestRef,
    navigateToPage,
    profileSettings,
    requestBudgetDraftEntry,
    reloadAvailableScopeYears,
    reloadBudgetRows,
    reloadExpenseRows,
    setAppSettings,
    setAvailableScopeYears,
    setBootThemePreference,
    setBudgetRows,
    setBudgetScope,
    setExpenseRows,
    setProfileSettings
  });

  const shortcutBindings = createAppShortcutBindings({
    appSettings,
    cycleToNextYear,
    handleLanguageChange,
    handleThemeChange,
    navigateSidebarSection,
    navigateToPage
  });

  useAppShortcuts(isDatabaseErrorMode ? [] : shortcutBindings);

  const scopeStartDate = useMemo(
    () => findScopeStartDate(budgetScope?.year, expenseRows),
    [budgetScope?.year, expenseRows]
  );

  const scopeDateContext = useMemo(
    () => createScopeDateContext(budgetScope?.year, new Date(), { scopeStartDate }),
    [budgetScope?.year, scopeStartDate]
  );

  const budgetPage = useMemo(
    () => createBudgetPageData(budgetRows, expenseRows, scopeDateContext),
    [budgetRows, expenseRows, scopeDateContext]
  );

  const headerTabs = useMemo(() => buildHeaderTabs(currentSections, t), [currentSections, t]);
  const headerAlert = useMemo(
    () => buildHeaderAlert({ updateAvailable, backupNeedsAttention, t }),
    [backupNeedsAttention, t, updateAvailable]
  );

  if (isDatabaseErrorMode) {
    return (
    <DatabaseErrorPage
      activeLanguage={activeLanguage}
      error={bootstrapError}
      handleLanguageChange={handleLanguageChange}
      t={t}
    />
  );
  }

  return (
    <AppShell
      currentPage={currentPage}
      tabs={headerTabs}
      alert={headerAlert}
      activeYear={budgetScope?.year ?? ""}
      yearOptions={availableScopeYears}
      onYearChange={handleHeaderYearChange}
      showAltHints={showAltHints}
      showViewportDebugBadge={SHOW_VIEWPORT_DEBUG_BADGE}
      viewportSize={viewportSize}
      shouldShowViewportGuard={shouldShowViewportGuard}
      minWidth={MIN_SUPPORTED_WIDTH}
      minHeight={MIN_SUPPORTED_HEIGHT}
      canRotateViewportToFit={canRotateViewportToFit}
      t={t}
    >
      <AppContent
        activeLanguage={activeLanguage}
        appSettings={appSettings}
        budgetPage={budgetPage}
        budgetDraftEntryRequest={budgetDraftEntryRequest}
        budgetScope={budgetScope}
        currentPage={currentPage}
        currentSections={currentSections}
        dateContext={scopeDateContext}
        expenseRows={expenseRows}
        handleAnalysisBudgetReviewFilterChange={handleAnalysisBudgetReviewFilterChange}
        handleAnalysisChartsMetricChange={handleAnalysisChartsMetricChange}
        handleAnalysisExplorerPresetChange={handleAnalysisExplorerPresetChange}
        handleBudgetRowsChange={handleBudgetRowsChange}
        handleBudgetViewModeChange={handleBudgetViewModeChange}
        handleCurrencyChange={handleCurrencyChange}
        handleDataImported={handleDataImported}
        handleExpensesChanged={handleExpensesChanged}
        handleExpensesVisibilityFilterChange={handleExpensesVisibilityFilterChange}
        handleHomeTileConfigsChange={handleHomeTileConfigsChange}
        handleImportPreviousYearBudget={handleImportPreviousYearBudget}
        handleLanguageChange={handleLanguageChange}
        handleLoadSampleData={handleLoadSampleData}
        handleStartBudgetEntry={handleStartBudgetEntry}
        hasPreviousYearBudgetRows={hasPreviousYearBudgetRows}
        handleScopeChanged={handleScopeChanged}
        handleThemeChange={handleThemeChange}
        isBudgetReady={isBudgetReady}
        getTranslationNode={getTranslationNodeForLanguage}
        navigateToPage={navigateToPage}
        navigateToSection={navigateToSection}
        profileSettings={profileSettings}
        showAltHints={showAltHints}
        t={t}
      />
    </AppShell>
  );
}
